import { GroupBusinessSettings, GroupTemplate } from '../../types';
import { describeQuery, parseQuery } from './query-builder-utils';

function describeSecurityQuery(query: string): string {
  if (!query) return 'No restrictions';
  const described = describeQuery(parseQuery(query));
  return described || query;
}

export interface DiffRow {
  field: DiffableField | 'securityQuery';
  label: string;
  currentValue: string;
  templateValue: string;
  changed: boolean;
}

export interface DiffSection {
  name: 'Basic information' | 'Settings' | 'Permissions' | 'Security';
  rows: DiffRow[];
  changedCount: number;
}

interface Option {
  displayValue: string;
  value: string;
}

export const primaryLanguageOptions: Option[] = [
  { displayValue: 'Arabic', value: 'Arabic' },
  { displayValue: 'Bengali', value: 'Bengali' },
  { displayValue: 'Bulgarian', value: 'Bulgarian' },
  { displayValue: 'Catalan', value: 'Catalan' },
  { displayValue: 'Chinese - Simplified', value: 'Chinese_Simplified' },
  { displayValue: 'Chinese - Traditional', value: 'Chinese_Traditional' },
  { displayValue: 'Croatian', value: 'Croatian' },
  { displayValue: 'Czech', value: 'Czech' },
  { displayValue: 'Danish', value: 'Danish' },
  { displayValue: 'Dutch', value: 'Dutch' },
  { displayValue: 'English - United States', value: 'English_United_States' },
  {
    displayValue: 'English - United Kingdom',
    value: 'English_United_Kingdom',
  },
  { displayValue: 'Estonian', value: 'Estonian' },
  { displayValue: 'Finnish', value: 'Finnish' },
  { displayValue: 'French', value: 'French' },
  { displayValue: 'German', value: 'German' },
  { displayValue: 'Greek', value: 'Greek' },
  { displayValue: 'Gujarati', value: 'Gujarati' },
  { displayValue: 'Hebrew', value: 'Hebrew' },
  { displayValue: 'Hindi', value: 'Hindi' },
  { displayValue: 'Hungarian', value: 'Hungarian' },
  { displayValue: 'Icelandic', value: 'Icelandic' },
  { displayValue: 'Indonesian', value: 'Indonesian' },
  { displayValue: 'Italian', value: 'Italian' },
  { displayValue: 'Japanese', value: 'Japanese' },
  { displayValue: 'Kannada', value: 'Kannada' },
  { displayValue: 'Korean', value: 'Korean' },
  { displayValue: 'Latvian', value: 'Latvian' },
  { displayValue: 'Lithuanian', value: 'Lituanian' },
  { displayValue: 'Malay', value: 'Malay' },
  { displayValue: 'Malayalam', value: 'Malayalam' },
  { displayValue: 'Marathi', value: 'Marathi' },
  { displayValue: 'Norwegian', value: 'Norewegian' },
  { displayValue: 'Oriya', value: 'Oriya' },
  { displayValue: 'Persian', value: 'Persian' },
  { displayValue: 'Polish', value: 'Polish' },
  { displayValue: 'Portuguese - Brazil', value: 'Portuguese_Brazil' },
  { displayValue: 'Portuguese - Portugal', value: 'Portuguese_Portugal' },
  { displayValue: 'Romanian', value: 'Romanian' },
  { displayValue: 'Russian', value: 'Russian' },
  { displayValue: 'Serbian', value: 'Serbian' },
  { displayValue: 'Slovak', value: 'Slovak' },
  { displayValue: 'Slovenian', value: 'Slovenian' },
  { displayValue: 'Spanish', value: 'Spanish' },
  { displayValue: 'Swedish', value: 'Swedish' },
  { displayValue: 'Tagalog', value: 'Tagalog' },
  { displayValue: 'Tamil', value: 'Tamil' },
  { displayValue: 'Telugu', value: 'Telugu' },
  { displayValue: 'Thai', value: 'Thai' },
  { displayValue: 'Turkish', value: 'Turkish' },
  { displayValue: 'Ukrainian', value: 'Ukranian' },
  { displayValue: 'Urdu', value: 'Urdu' },
  { displayValue: 'Vietnamese', value: 'Vietnamese' },
];

export const moderationLevelOptions: Option[] = [
  { displayValue: 'Moderate all messages', value: 'MODERATE_ALL_MESSAGES' },
  {
    displayValue: 'Moderate messages by new members',
    value: 'MODERATE_NEW_MEMBERS',
  },
  {
    displayValue: 'Moderate messages by non members',
    value: 'MODERATE_NON_MEMBERS',
  },
  { displayValue: `Don't moderate messages`, value: 'MODERATE_NONE' },
];

export const spamModerationLevelOptions: Option[] = [
  {
    displayValue:
      'Send them to the moderation queue and send notification to moderators',
    value: 'MODERATE',
  },
  {
    displayValue:
      'Send them to the moderation queue, but do not send notification to the moderators',
    value: 'SILENTLY_MODERATE',
  },
  { displayValue: 'Immediately reject them', value: 'REJECT' },
  {
    displayValue: 'Skip the moderation queue and post to the group',
    value: 'ALLOW',
  },
];

export const replyToOptions: Option[] = [
  { displayValue: 'To the entire group', value: 'REPLY_TO_LIST' },
  {
    displayValue: 'To the author of the message only',
    value: 'REPLY_TO_SENDER',
  },
  { displayValue: 'To the owners of the group', value: 'REPLY_TO_OWNER' },
  {
    displayValue: 'To the managers of the group',
    value: 'REPLY_TO_MANAGERS',
  },
  {
    displayValue: 'Users decide where their replies are sent',
    value: 'REPLY_TO_IGNORE',
  },
  {
    displayValue: 'Use a custom address to send replies to',
    value: 'REPLY_TO_CUSTOM',
  },
];

export const whoCanViewGroupOptions: Option[] = [
  { displayValue: 'Group owners', value: 'ALL_OWNERS_CAN_VIEW' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_VIEW' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_VIEW' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_VIEW' },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_VIEW' },
];

export const whoCanPostMessageOptions: Option[] = [
  { displayValue: 'None can post', value: 'NONE_CAN_POST' },
  { displayValue: 'Group owners', value: 'ALL_OWNERS_CAN_POST' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_POST' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_POST' },
  {
    displayValue: 'Entire organization',
    value: 'ALL_IN_DOMAIN_CAN_POST',
  },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_POST' },
];

export const whoCanViewMembersOptions: Option[] = [
  { displayValue: 'Group Owners', value: 'ALL_OWNERS_CAN_VIEW' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_VIEW' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_VIEW' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_VIEW' },
];

export const whoCanModerateMembersOptions: Option[] = [
  { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
  { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS' },
  { displayValue: 'None', value: 'NONE' },
];

export const whoCanModerateContentOptions: Option[] = [
  { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
  { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS' },
  { displayValue: 'None', value: 'NONE' },
];

export const whoCanAssistContentOptions: Option[] = [
  { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
  { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS' },
  { displayValue: 'None', value: 'NONE' },
];

export const whoCanLeaveGroupOptions: Option[] = [
  { displayValue: 'Group Owners', value: 'ALL_OWNERS_CAN_LEAVE' },
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_LEAVE' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_LEAVE' },
  { displayValue: 'None', value: 'NONE_CAN_LEAVE' },
];

export const inviteMembersOptions: Option[] = [
  { displayValue: 'Anyone on the web can join', value: 'ANYONE_CAN_JOIN' },
  {
    displayValue: 'Organization users only',
    value: 'ALL_IN_DOMAIN_CAN_JOIN',
  },
  {
    displayValue: 'Organization users can ask',
    value: 'CAN_REQUEST_TO_JOIN',
  },
  { displayValue: 'Invited users only', value: 'INVITED_CAN_JOIN' },
];

export const whoCanContactOwnerOptions: Option[] = [
  { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_CONTACT' },
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_CONTACT' },
  { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_CONTACT' },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_CONTACT' },
];

export const whoCanDiscoverGroupOptions: Option[] = [
  { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_DISCOVER' },
  {
    displayValue: 'Entire organization',
    value: 'ALL_IN_DOMAIN_CAN_DISCOVER',
  },
  { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_DISCOVER' },
];

type FieldKind =
  | { type: 'boolean' }
  | { type: 'text' }
  | { type: 'options'; options: Option[] };

type DiffableField = Exclude<
  keyof GroupBusinessSettings,
  'name' | 'email' | 'description'
>;

interface FieldSpec {
  field: DiffableField;
  label: string;
  section: 'Basic information' | 'Settings' | 'Permissions';
  kind: FieldKind;
}

const FIELD_SPECS: FieldSpec[] = [
  {
    field: 'primaryLanguage',
    label: 'Primary Language',
    section: 'Basic information',
    kind: { type: 'options', options: primaryLanguageOptions },
  },
  {
    field: 'allowWebPosting',
    label: 'All users to post on the web',
    section: 'Basic information',
    kind: { type: 'boolean' },
  },
  {
    field: 'includeInGlobalAddressList',
    label: 'Include in Global Address Lists',
    section: 'Basic information',
    kind: { type: 'boolean' },
  },
  {
    field: 'isArchived',
    label: 'Archive messages to the group',
    section: 'Basic information',
    kind: { type: 'boolean' },
  },
  {
    field: 'membersCanPostAsTheGroup',
    label: 'Allow members to post as the group',
    section: 'Settings',
    kind: { type: 'boolean' },
  },
  {
    field: 'messageModerationLevel',
    label: 'Moderate Messages',
    section: 'Settings',
    kind: { type: 'options', options: moderationLevelOptions },
  },
  {
    field: 'sendMessageDenyNotification',
    label: 'Notify authors when moderators reject their posts',
    section: 'Settings',
    kind: { type: 'boolean' },
  },
  {
    field: 'defaultMessageDenyNotificationText',
    label: 'Rejection Message',
    section: 'Settings',
    kind: { type: 'text' },
  },
  {
    field: 'spamModerationLevel',
    label: 'Spam Messages',
    section: 'Settings',
    kind: { type: 'options', options: spamModerationLevelOptions },
  },
  {
    field: 'replyTo',
    label: 'Post Replies',
    section: 'Settings',
    kind: { type: 'options', options: replyToOptions },
  },
  {
    field: 'customReplyTo',
    label: 'Custom Address',
    section: 'Settings',
    kind: { type: 'text' },
  },
  {
    field: 'whoCanViewGroup',
    label: 'Who can view conversations',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanViewGroupOptions },
  },
  {
    field: 'whoCanPostMessage',
    label: 'Who can post',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanPostMessageOptions },
  },
  {
    field: 'whoCanViewMembership',
    label: 'Who can view members',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanViewMembersOptions },
  },
  {
    field: 'whoCanModerateMembers',
    label: 'Who can manage members',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanModerateMembersOptions },
  },
  {
    field: 'whoCanModerateContent',
    label: 'Who can moderate content',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanModerateContentOptions },
  },
  {
    field: 'whoCanAssistContent',
    label: 'Who can assist content',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanAssistContentOptions },
  },
  {
    field: 'whoCanContactOwner',
    label: 'Who can contact group owners',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanContactOwnerOptions },
  },
  {
    field: 'whoCanLeaveGroup',
    label: 'Who can leave group',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanLeaveGroupOptions },
  },
  {
    field: 'whoCanDiscoverGroup',
    label: 'Who can discover group',
    section: 'Permissions',
    kind: { type: 'options', options: whoCanDiscoverGroupOptions },
  },
  {
    field: 'whoCanJoin',
    label: 'Who can join group',
    section: 'Permissions',
    kind: { type: 'options', options: inviteMembersOptions },
  },
  {
    field: 'allowExternalMembers',
    label: 'Allow new users not in domain',
    section: 'Permissions',
    kind: { type: 'boolean' },
  },
];

function formatValue(value: unknown, kind: FieldKind): string {
  if (value === undefined || value === null || value === '') {
    return kind.type === 'boolean' ? 'Disabled' : '—';
  }
  if (kind.type === 'boolean') {
    return value ? 'Enabled' : 'Disabled';
  }
  if (kind.type === 'options') {
    const match = kind.options.find((option) => option.value === value);
    return match ? match.displayValue : String(value);
  }
  return String(value);
}

export function describeTemplateSettings(
  template: GroupTemplate
): DiffSection[] {
  const sections: Record<DiffSection['name'], DiffRow[]> = {
    'Basic information': [],
    Settings: [],
    Permissions: [],
    Security: [],
  };

  for (const spec of FIELD_SPECS) {
    sections[spec.section].push({
      field: spec.field,
      label: spec.label,
      currentValue: '',
      templateValue: formatValue(template[spec.field], spec.kind),
      changed: false,
    });
  }

  sections.Security.push({
    field: 'securityQuery',
    label: 'Member restriction',
    currentValue: '',
    templateValue: describeSecurityQuery(template.securityQuery ?? ''),
    changed: false,
  });

  return (
    ['Basic information', 'Settings', 'Permissions', 'Security'] as const
  ).map((name) => ({
    name,
    rows: sections[name],
    changedCount: 0,
  }));
}

export function computeSettingsDiff(
  currentSettings: GroupBusinessSettings,
  template: GroupTemplate,
  currentSecurityQuery: string
): DiffSection[] {
  const sections: Record<DiffSection['name'], DiffRow[]> = {
    'Basic information': [],
    Settings: [],
    Permissions: [],
    Security: [],
  };

  for (const spec of FIELD_SPECS) {
    const currentValue = currentSettings[spec.field];
    const templateValue = template[spec.field];
    const currentDisplay = formatValue(currentValue, spec.kind);
    const templateDisplay = formatValue(templateValue, spec.kind);
    sections[spec.section].push({
      field: spec.field,
      label: spec.label,
      currentValue: currentDisplay,
      templateValue: templateDisplay,
      changed: currentDisplay !== templateDisplay,
    });
  }

  const templateSecurityQuery = template.securityQuery ?? '';
  const currentSecurityDisplay = describeSecurityQuery(currentSecurityQuery);
  const templateSecurityDisplay = describeSecurityQuery(templateSecurityQuery);
  sections.Security.push({
    field: 'securityQuery',
    label: 'Member restriction',
    currentValue: currentSecurityDisplay,
    templateValue: templateSecurityDisplay,
    changed: currentSecurityDisplay !== templateSecurityDisplay,
  });

  return (
    ['Basic information', 'Settings', 'Permissions', 'Security'] as const
  ).map((name) => ({
    name,
    rows: sections[name],
    changedCount: sections[name].filter((row) => row.changed).length,
  }));
}

export function buildChangedBusinessSettings(
  currentSettings: GroupBusinessSettings,
  template: GroupTemplate,
  sections: DiffSection[]
): GroupBusinessSettings {
  const changedFields = new Set(
    sections
      .filter((section) => section.name !== 'Security')
      .flatMap((section) => section.rows)
      .filter((row) => row.changed)
      .map((row) => row.field)
  );

  const updated = { ...currentSettings } as unknown as Record<string, unknown>;
  for (const spec of FIELD_SPECS) {
    if (changedFields.has(spec.field)) {
      updated[spec.field] = template[spec.field];
    }
  }
  return updated as unknown as GroupBusinessSettings;
}

/**
 * True if any field the diff table/apply flow cares about differs between two copies of the
 * same template — used to detect a template edited elsewhere after it was selected but before
 * the reviewed preview was applied.
 */
export function haveTemplateSettingsChanged(
  reviewed: GroupTemplate,
  current: GroupTemplate
): boolean {
  if ((reviewed.securityQuery ?? '') !== (current.securityQuery ?? '')) {
    return true;
  }
  return FIELD_SPECS.some(
    (spec) => reviewed[spec.field] !== current[spec.field]
  );
}

export function sortGroupTemplatesByName(
  templates: GroupTemplate[]
): GroupTemplate[] {
  return [...templates].sort((a, b) =>
    (a.groupTemplateName ?? '').localeCompare(
      b.groupTemplateName ?? '',
      undefined,
      { sensitivity: 'base', numeric: true }
    )
  );
}
